/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Component, Input, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Subject, takeUntil } from "rxjs";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { TableModule } from "primeng/table";
import { ButtonModule } from "primeng/button";
import { InputNumberModule } from "primeng/inputnumber";
import { AutoCompleteModule } from "primeng/autocomplete";
import { CardModule } from "primeng/card";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { TooltipModule } from "primeng/tooltip";
import { ConfirmationService } from "primeng/api";
import {
  QPilotService,
  QPilotAccessToken,
} from "../../../../../core/services/qpilot.service";
import { SessionService } from "../../../../../core/services/session.service";
import { ScheduledOrderItem } from "../../../../../core/models/scheduled-order-item.interface";
import { ScheduledOrderItemForm } from "../../../interfaces/scheduled-order-form.interface";

/**
 * Item manager component for adding, editing, and removing scheduled order items
 * Used in scheduled order create/edit forms to manage the product list
 */
@Component({
  selector: "app-item-manager",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    InputNumberModule,
    AutoCompleteModule,
    CardModule,
    ConfirmDialogModule,
    TooltipModule,
  ],
  providers: [ConfirmationService],
  templateUrl: "./item-manager.component.html",
  styleUrl: "./item-manager.component.scss",
})
export class ItemManagerComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @Input() itemsFormArray!: FormArray<FormGroup<ScheduledOrderItemForm>>;
  @Input() siteId!: number;
  @Input() disabled = false;
  @Input() required = false;
  @Input() mode: "create" | "edit" = "create"; // Edit mode enables item disable/enable toggle
  @Input() accessToken?: QPilotAccessToken; // Optional token for embedded mode

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filteredProducts: any[] = []; // TODO: Replace with Product[] when interface available
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedProduct?: any; // TODO: Replace with Product interface
  isSearching = false;

  // Track expanded rows for progressive disclosure
  expandedItems = new Set<number>();

  constructor(
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private confirmationService: ConfirmationService,
    private formBuilder: FormBuilder,
  ) {}

  /**
   * Create a FormGroup for a scheduled order item
   * @param item Optional item to populate form with existing values
   * @returns FormGroup for the item
   */
  private createItemFormGroup(
    item?: ScheduledOrderItem,
  ): FormGroup<ScheduledOrderItemForm> {
    return this.formBuilder.nonNullable.group({
      id: [item?.Id ?? null],
      scheduledOrderId: [item?.ScheduledOrderId ?? null],
      productId: [item?.ProductId || 0],
      quantity: [item?.Quantity || 1],
      price: [item?.Price || 0],
      salePrice: [item?.SalePrice ?? undefined],
      originalSalePrice: [item?.OriginalSalePrice ?? null],
      product: [item?.Product ?? null],
      disabled: [item?.Disabled ?? false],
      cycles: [item?.Cycles ?? undefined],
      minCycles: [item?.MinCycles ?? undefined],
      maxCycles: [item?.MaxCycles ?? undefined],
      createdUtc: [
        item?.CreatedUtc ? new Date(item.CreatedUtc).toISOString() : null,
      ],
      updatedUtc: [
        item?.UpdatedUtc ? new Date(item.UpdatedUtc).toISOString() : null,
      ],
      metadata: [item?.Metadata ?? null],
    }) as unknown as FormGroup<ScheduledOrderItemForm>;
  }

  /**
   * Handle focus event on autocomplete to fetch all products
   * Triggered when user clicks on the dropdown or field
   */
  onProductFocus(): void {
    // If field is empty and no products loaded yet, fetch all products
    if (!this.selectedProduct && this.filteredProducts.length === 0) {
      this.search({ query: "" });
    }
  }

  /**
   * Search for products based on user input
   * @param event AutoComplete search event
   */
  search(event: { query: string }): void {
    const query = event.query;

    // When query is empty (dropdown click), fetch all products from page 1
    // When typing, require at least 2 characters for search
    if (query.length > 0 && query.length < 2) {
      this.filteredProducts = [];
      return;
    }

    this.isSearching = true;

    const token = this.accessToken ?? this.sessionService.accessToken;
    if (!token) {
      this.isSearching = false;
      return;
    }

    this.qpilotService
      .getProductsForSite(
        this.siteId,
        token,
        1,
        query || undefined, // Pass undefined for empty query to fetch all products
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (products) => {
          // Filter out variable products (matches old QMC line 201 & 443)
          // Variable products are parent products with variations - only variations can be added to orders
          this.filteredProducts = products.filter((p) => p.Type !== "variable");
          this.isSearching = false;
        },
        error: () => {
          this.filteredProducts = [];
          this.isSearching = false;
        },
      });
  }

  /**
   * Add a product to the items list
   * @param product Selected product from autocomplete
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onProductSelect(event: any): void {
    if (!event) return;

    // PrimeNG AutoComplete onSelect passes an event object with the selected value
    // The actual product is in event.value, not in event directly
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const product = event.value || event;
    if (!product) return;

    // Check if product already exists in items
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const existingItemIndex = this.itemsFormArray.controls.findIndex(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (itemControl) => itemControl.value.productId === product.Id,
    );

    if (existingItemIndex !== -1) {
      // Increment quantity if already exists
      const existingItem = this.itemsFormArray.at(existingItemIndex);
      const currentQuantity = existingItem.value.quantity || 0;
      existingItem.patchValue({
        quantity: currentQuantity + 1,
      });
    } else {
      // Add new item
      // Use SalePrice if available, otherwise Price (matches old QMC behavior)
      const effectivePrice = product.SalePrice || product.Price || 0; // eslint-disable-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const newItem: ScheduledOrderItem = {
        Id: 0, // Temporary ID for new items
        ProductId: product.Id, // eslint-disable-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        ProductName: product.Title, // eslint-disable-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        Product: product, // eslint-disable-line @typescript-eslint/no-unsafe-assignment -- Include full Product object (required by API)
        Quantity: 1,
        Price: product.Price || 0, // eslint-disable-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        SalePrice: product.SalePrice || undefined, // eslint-disable-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        Total: effectivePrice, // eslint-disable-line @typescript-eslint/no-unsafe-assignment
        Disabled: false, // New items are enabled by default
        // Match old QMC: leave Cycles/MinCycles/MaxCycles undefined (not 0)
        // They will only be included in API payload if user explicitly sets them
        Cycles: undefined,
        MinCycles: undefined,
        MaxCycles: undefined,
      };

      // Push new FormGroup to FormArray
      this.itemsFormArray.push(this.createItemFormGroup(newItem));
    }

    // Clear selection and recalculate
    this.selectedProduct = undefined;
    this.recalculateItemTotals();
    // Form automatically syncs - no need to emit
  }

  /**
   * Get the actual index of an item control in the full FormArray
   * @param itemControl The form control to find
   * @returns The index in itemsFormArray, or -1 if not found
   */
  private getActualIndex(
    itemControl: FormGroup<ScheduledOrderItemForm>,
  ): number {
    return this.itemsFormArray.controls.indexOf(itemControl);
  }

  /**
   * Update item quantity
   * @param itemControl The item FormGroup to update
   * @param quantity New quantity value
   */
  onQuantityChange(
    itemControl: FormGroup<ScheduledOrderItemForm>,
    quantity: number | null,
  ): void {
    const actualIndex = this.getActualIndex(itemControl);
    if (actualIndex === -1) return;

    if (quantity === null || quantity < 1) {
      // Remove item if quantity becomes 0 or invalid
      this.removeItem(actualIndex);
      return;
    }

    if (!itemControl) return;

    itemControl.patchValue({ quantity });
    this.recalculateItemTotals();
    // Form automatically syncs - no need to emit
  }

  /**
   * Update item price
   * @param itemControl The item FormGroup to update
   * @param price New price value
   */
  onPriceChange(
    itemControl: FormGroup<ScheduledOrderItemForm>,
    price: number | null,
  ): void {
    if (!itemControl) return;

    itemControl.patchValue({
      price: price === null || price < 0 ? 0 : price,
    });
    this.recalculateItemTotals();
    // Form automatically syncs - no need to emit
  }

  /**
   * Update item sale price
   * @param itemControl The item FormGroup to update
   * @param salePrice New sale price value
   */
  onSalePriceChange(
    itemControl: FormGroup<ScheduledOrderItemForm>,
    salePrice: number | null,
  ): void {
    if (!itemControl) return;

    itemControl.patchValue({
      salePrice: salePrice === null || salePrice < 0 ? undefined : salePrice,
    });
    this.recalculateItemTotals();
    // Form automatically syncs - no need to emit
  }

  /**
   * Update item cycles
   * @param itemControl The item FormGroup to update
   * @param cycles New cycles value
   */
  onCyclesChange(
    itemControl: FormGroup<ScheduledOrderItemForm>,
    cycles: number | null,
  ): void {
    if (!itemControl) return;

    itemControl.patchValue({
      cycles: cycles === null || cycles < 0 ? 0 : cycles,
    });
    // Form automatically syncs - no need to emit
  }

  /**
   * Update item minimum cycles
   * @param itemControl The item FormGroup to update
   * @param minCycles New minimum cycles value
   */
  onMinCyclesChange(
    itemControl: FormGroup<ScheduledOrderItemForm>,
    minCycles: number | null,
  ): void {
    if (!itemControl) return;

    itemControl.patchValue({
      minCycles: minCycles === null || minCycles < 0 ? 0 : minCycles,
    });
    // Form automatically syncs - no need to emit
  }

  /**
   * Update item maximum cycles
   * @param itemControl The item FormGroup to update
   * @param maxCycles New maximum cycles value
   */
  onMaxCyclesChange(
    itemControl: FormGroup<ScheduledOrderItemForm>,
    maxCycles: number | null,
  ): void {
    if (!itemControl) return;

    itemControl.patchValue({
      maxCycles: maxCycles === null || maxCycles < 0 ? 0 : maxCycles,
    });
    // Form automatically syncs - no need to emit
  }

  /**
   * Remove an item from the list
   * @param index Index of item to remove
   */
  removeItem(index: number): void {
    const itemControl = this.itemsFormArray.at(index);
    if (!itemControl || !itemControl.value) {
      return;
    }

    const productName = itemControl.value.product?.Title || "this product";

    this.confirmationService.confirm({
      message: `Remove ${productName} from order?`,
      header: "Confirm Removal",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        this.itemsFormArray.removeAt(index);
        // Form automatically syncs - no need to emit
      },
    });
  }

  /**
   * Toggle the disabled state of an item (edit mode only)
   * @param index Index of item to toggle
   */
  toggleItemDisabled(index: number): void {
    const itemControl = this.itemsFormArray.at(index);
    const currentDisabled = itemControl.value.disabled || false;
    itemControl.patchValue({ disabled: !currentDisabled });
    this.recalculateItemTotals();
    // Form automatically syncs - no need to emit
  }

  /**
   * Get display label for a product in the dropdown
   * @param product Product object
   * @returns Formatted display string
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getProductLabel(product: any): string {
    if (!product) return "";

    const parts: string[] = [];

    // Add product title
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (product.Title) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
      parts.push(product.Title);
    }

    // Add SKU if available
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (product.Sku) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      parts.push(`(${product.Sku})`);
    }

    // Add price
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (product.Price !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      parts.push(`- $${product.Price.toFixed(2)}`);
    }

    return parts.join(" ");
  }

  /**
   * Recalculate totals for all items based on quantity and price
   * Uses SalePrice if available, otherwise uses Price (matches old QMC behavior)
   * NOTE: With reactive forms, totals are calculated on-demand, not stored.
   * This method is kept for API compatibility but doesn't need to do anything.
   * The parent form wrapper handles totals calculation.
   */
  private recalculateItemTotals(): void {
    // Form automatically syncs - parent will recalculate totals when needed
  }

  /**
   * Get the total of all enabled items at regular price (before discount)
   * @returns Sum of (quantity * regular price) for all enabled items
   */
  getItemsTotalBeforeDiscount(): number {
    let total = 0;
    this.itemsFormArray.controls.forEach((itemControl) => {
      const item = itemControl.value;
      const product = item.product;

      // Only include items that are NOT disabled
      const isDisabled =
        !product ||
        !this.productIsActive(product) ||
        this.disabledForBackorders(item);

      if (!isDisabled) {
        // Use regular price (not sale price)
        const regularPrice = item.price || 0;
        const itemTotal = (item.quantity || 0) * regularPrice;
        total += itemTotal;
      }
    });
    return total;
  }

  /**
   * Get the total price discount from sale prices
   * @returns Sum of savings from sale prices (negative value)
   */
  getPriceDiscount(): number {
    let discount = 0;
    this.itemsFormArray.controls.forEach((itemControl) => {
      const item = itemControl.value;
      const product = item.product;

      // Only include items that are NOT disabled
      const isDisabled =
        !product ||
        !this.productIsActive(product) ||
        this.disabledForBackorders(item);

      if (
        !isDisabled &&
        item.salePrice !== undefined &&
        item.salePrice !== null
      ) {
        // Calculate savings: (regular price - sale price) * quantity
        const regularPrice = item.price || 0;
        const savings = (regularPrice - item.salePrice) * (item.quantity || 0);
        discount += savings;
      }
    });
    // Return as negative value to show as discount
    return -discount;
  }

  /**
   * Get the grand total of all items (excluding disabled items)
   * @returns Sum of all enabled item totals (after applying sale prices)
   */
  getGrandTotal(): number {
    let total = 0;
    this.itemsFormArray.controls.forEach((itemControl) => {
      const item = itemControl.value;
      const product = item.product;

      // Only include items that are NOT disabled
      // Use the same logic as getDisabledItems() to determine if item is disabled
      const isDisabled =
        !product ||
        !this.productIsActive(product) ||
        this.disabledForBackorders(item);

      if (!isDisabled) {
        // Only count enabled items in total
        const effectivePrice = item.salePrice || item.price || 0;
        const itemTotal = (item.quantity || 0) * effectivePrice;
        total += itemTotal;
      }
    });
    return total;
  }

  /**
   * Toggle the expansion state of a table row
   * @param itemControl The item FormGroup to toggle
   */
  toggleRow(itemControl: FormGroup<ScheduledOrderItemForm>): void {
    if (!itemControl || !itemControl.value) {
      return;
    }
    const productId = itemControl.value.productId;
    if (productId === undefined) {
      return;
    }

    if (this.expandedItems.has(productId)) {
      this.expandedItems.delete(productId);
    } else {
      this.expandedItems.add(productId);
    }
  }

  /**
   * Check if a row is expanded
   * @param itemControl The item FormGroup to check
   * @returns True if row is expanded
   */
  isRowExpanded(itemControl: FormGroup<ScheduledOrderItemForm>): boolean {
    if (!itemControl || !itemControl.value) return false;
    const productId = itemControl.value.productId;
    if (productId === undefined) return false;
    return this.expandedItems.has(productId);
  }

  /**
   * Get enabled items (items that can be processed)
   * Matches old QMC logic (lines 188-209)
   * @returns Array of enabled item FormGroups
   */
  getEnabledItems(): FormGroup<ScheduledOrderItemForm>[] {
    return this.itemsFormArray.controls.filter((itemControl) => {
      const item = itemControl.value;
      const product = item.product;
      if (!product) return false;
      return this.productIsActive(product) && !this.disabledForBackorders(item);
    });
  }

  /**
   * Get disabled items (items that cannot be processed)
   * Matches old QMC logic (lines 188-209)
   * @returns Array of disabled item FormGroups
   */
  getDisabledItems(): FormGroup<ScheduledOrderItemForm>[] {
    return this.itemsFormArray.controls.filter((itemControl) => {
      const item = itemControl.value;
      const product = item.product;
      if (!product) return false;
      return !this.productIsActive(product) || this.disabledForBackorders(item);
    });
  }

  /**
   * Check if an item should be included in calculations (not disabled)
   * Uses the same logic as getEnabledItems() and getGrandTotal()
   * @param item Scheduled order item
   * @returns True if item should be included in calculations
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isItemEnabled(item: any): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const product = item.product;
    if (!product) return false;
    return this.productIsActive(product) && !this.disabledForBackorders(item);
  }

  /**
   * Check if product is active (can be processed)
   * Matches old QMC lines 398-408
   * @param product Product object
   * @returns True if product is active
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  productIsActive(product: any): boolean {
    return (
      this.productIsInStock(product) &&
      this.productIsProcessing(product) &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      product.Active &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      product.Valid
    );
  }

  /**
   * Check if product is in stock
   * Matches old QMC lines 402-404
   * @param product Product object
   * @returns True if product is in stock
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  productIsInStock(product: any): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      product.StockLevel === "Undefined" || product.StockLevel === "InStock"
    );
  }

  /**
   * Check if product is enabled for processing scheduled orders
   * Matches old QMC lines 406-408
   * @param product Product object
   * @returns True if product can process scheduled orders
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  productIsProcessing(product: any): boolean {
    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      product.Availability &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      product.Availability.indexOf("ProcessScheduledOrder") > -1
    );
  }

  /**
   * Check if item is disabled due to backorder restrictions
   * Matches old QMC lines 1579-1582
   * @param item Scheduled order item
   * @returns True if item is disabled for backorders
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  disabledForBackorders(item: any): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const product = item.product;
    if (!product) return false;

    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      product.BackordersAllowed != null &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      !product.BackordersAllowed &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      product.Stock != null &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      product.Stock < item.quantity
    );
  }

  /**
   * Get the reason why an item is disabled
   * Matches old QMC tooltip logic (lines 387-460)
   * @param item Scheduled order item
   * @returns Human-readable reason for disable state
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDisabledReason(item: any): string {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const product = item.product;
    if (!product) return "Product information unavailable";

    // Check each condition in priority order (matches old QMC)
    if (this.disabledForBackorders(item)) {
      return "Backorders not allowed and quantity exceeds stock. Item will be enabled when stock is sufficient.";
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (!product.Active) {
      return "Product is inactive. Item will be enabled when product becomes active.";
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (!product.Valid) {
      return "Product is invalid. Item will be enabled when product becomes valid.";
    }
    if (!this.productIsInStock(product)) {
      return "This item is Out of Stock and will not process until it is In Stock again. Out of Stock items do not count towards the subtotal value of the Scheduled Order.";
    }
    if (!this.productIsProcessing(product)) {
      return "Not enabled for processing. Disabled items do not count towards the subtotal.";
    }

    return "Item is disabled for processing";
  }

  /**
   * Get the icon name for a disabled item
   * Matches old QMC icon logic (lines 387-460)
   * @param item Scheduled order item
   * @returns Material icon name
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDisabledIcon(item: any): string {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const product = item.product;
    if (!product) return "pi-exclamation-triangle";

    // Not enabled for processing -> different icon (matches old QMC line 446)
    if (!this.productIsProcessing(product)) {
      return "pi-sync-disabled"; // sync_disabled equivalent
    }

    // All other cases -> do_not_disturb icon (matches old QMC lines 404, 413, 421, 430)
    return "pi-ban"; // do_not_disturb equivalent (ban/circle-slash)
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
